import { SearchOutlined } from '@ant-design/icons';
import { Input, InputProps, theme } from 'antd';
import type { InputRef } from 'antd/es/input';
import { forwardRef, useImperativeHandle, useRef } from 'react';

type SearchInputProps = {
  inputSearchProps?: InputProps;
  onSearch?: (value: string) => void;
  dropdownOpen?: boolean;
};

const SearchInput = forwardRef<InputRef, SearchInputProps>(({ inputSearchProps, onSearch, dropdownOpen }, ref) => {
  const { token } = theme.useToken();

  const inputRef = useRef<InputRef | null>(null);
  const { onChange: inputOnChange, ...restInputProps } = inputSearchProps ?? {};

  useImperativeHandle(ref, () => ({
    focus: (options) => inputRef.current?.focus(options),
    blur: () => inputRef.current?.blur(),
    select: () => inputRef.current?.select(),
    setSelectionRange: (start: number, end: number, direction?: 'forward' | 'backward' | 'none') =>
      inputRef.current?.setSelectionRange(start, end, direction),
    input: inputRef.current?.input ?? null,
    nativeElement: inputRef.current?.nativeElement ?? null,
  }));

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent backspace and other keys from bubbling up to the Select
    e.stopPropagation();
  };

  return (
    <Input
      {...restInputProps}
      allowClear
      ref={inputRef}
      autoFocus={dropdownOpen}
      onChange={(event) => {
        inputOnChange?.(event);
        onSearch?.(event.target.value.trim());
      }}
      onKeyDown={handleKeyDown}
      onClear={() => onSearch?.('')}
      prefix={
        <SearchOutlined
          style={{
            color: token.colorIcon,
            fontSize: token.fontSizeSM,
          }}
        />
      }
    />
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;
